'use client';

import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { buildResizeUrl } from '@ps/functions/utils/image-loader';
import { PopImage } from '@ps/ui/atoms/pop-image';
import { cn } from '@ps/ui/utils';
import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

// Slide resize: `fit: 'inside'` with a generous height ceiling so the resize
// CDN scales the source proportionally (never crops portrait media) — the PDP
// carousel needs the full image for its `object-contain` letterboxing.
const SLIDE_RESIZE = { width: 1344, height: 1344 * 3, fit: 'inside' as const };

// Full-screen lightbox source — full aspect ratio, capped at 1600px (matches
// the retired hand-rolled ImageViewer).
const VIEWER_RESIZE = { width: 1600, height: 1600, fit: 'inside' as const };

/**
 * Slide payload — at minimum a `url`; richer fields let the PDP
 * Wear-With-AI integration tag a slide as an AI-generated media
 * item so the overlay chips know which slide they're acting on.
 *
 * `tag === 'avatar-me'` activates the favorite/visibility chips
 * for the slide owner (the chips themselves are rendered by the
 * caller via `slideOverlay`).
 */
export type CarouselMedia = {
  url: string;
  /** Optional unique id — required for AI media so favorite /
   *  visibility mutations have a target. */
  id?: string;
  type?: 'image' | 'video';
  tag?: 'default' | 'avatar-me';
  favorite?: boolean;
  visible?: boolean;
};

type ProductImageCarouselProps = {
  /** Legacy string-only media — kept for callers that don't need
   *  the richer per-slide metadata. Wins when `media` is omitted. */
  images?: string[];
  /** Rich media list with per-slide tags. Takes precedence over
   *  `images` when supplied. The PDP passes this so the Wear-With-AI
   *  slides can be merged into the carousel + tagged. */
  media?: CarouselMedia[];
  name: string;
  /**
   * Free slot pinned to the top-right of the hero — mirrors RN's
   * `floatingActions` (the live-show gift button). Empty on the standalone
   * PDP today; the slot exists so the future live-show wrapper can inject
   * it without touching the carousel.
   */
  floatingActions?: ReactNode;
  /** Optional per-slide overlay — invoked for the ACTIVE slide
   *  only. The PDP Wear-With-AI integration uses this to render
   *  favorite + visibility chips on the current AI slide. */
  slideOverlay?: (media: CarouselMedia, index: number) => ReactNode;
};

/**
 * Square product hero with carousel + fullscreen zoom. The image is shown
 * `object-contain` so portrait/landscape art is never cropped; a heavily
 * blurred copy of the same image fills the letterboxed area as a backdrop —
 * mirrors the RN PDP / `feat/product-surfaces` gallery exactly.
 *
 * Navigation is a native horizontal **scroll-snap track**: every slide is
 * mounted side-by-side in an `overflow-x` scroller with `snap-x mandatory`,
 * so mouse-drag / trackpad / touch / wheel all change the image for free —
 * and because every slide is already in the DOM there's no swap-flash when
 * you navigate (the old approach swapped one `<img>`'s `src`, which showed
 * the previous decoded image until the new one loaded). The prev/next
 * buttons and dots `scrollTo` the target slide; a scroll listener keeps the
 * active dot + blurred backdrop in sync.
 *
 * Images render through `PopImage` with `resizing` (the resize-CDN URL is built
 * up front, so listing media on ANY host works without `next.config`
 * `remotePatterns`). The first slide is the LCP candidate — `priority` so Next
 * emits `fetchpriority="high"` (PopImage passes no custom loader, which would
 * otherwise suppress it).
 */
export function ProductImageCarousel({
  images,
  media,
  name,
  floatingActions,
  slideOverlay,
}: ProductImageCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  // Resolve to a unified `slides` list. When `media` is passed it
  // wins; otherwise project `images` into the same shape.
  const slides: CarouselMedia[] =
    media && media.length > 0
      ? media
      : (images ?? []).map(url => ({ url, type: 'image' as const }));

  const count = slides.length;
  const safeIndex = Math.min(index, Math.max(0, count - 1));
  const activeSlide = slides[safeIndex];
  const current = activeSlide?.url;

  /**
   * Legacy hero-height formula (`src/flows/pdp/functions/usePdp.ts`):
   *
   *   max( 303, min( containerWidth, viewportHeight * 0.4 ) )
   *
   * Caps the hero so it never exceeds its own width — important for
   * desktop where 40vh alone would let portrait media stretch the
   * box tall and crowd out the buy CTA. On narrow / phone widths
   * the formula collapses to the width or the 303px floor, matching
   * RN's layout. Recomputed on resize via `ResizeObserver`.
   */
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [heroHeight, setHeroHeight] = useState<number | null>(null);
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const compute = () => {
      const w = el.clientWidth;
      const vh = window.innerHeight;
      setHeroHeight(Math.max(303, Math.min(w, vh * 0.4)));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    window.addEventListener('resize', compute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, []);

  // Keep the active index synced with the scroll position (drag / wheel /
  // swipe). Computed from scrollLeft so it tracks free-scroll mid-gesture.
  const onScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el || el.clientWidth === 0) return;
    const next = Math.round(el.scrollLeft / el.clientWidth);
    setIndex(prev => (prev === next ? prev : next));
  }, []);

  const scrollTo = useCallback((i: number) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  }, []);

  const prev = () => scrollTo((index - 1 + count) % count);
  const next = () => scrollTo((index + 1) % count);

  return (
    <PhotoProvider>
      <div
        ref={wrapperRef}
        /* Hero height = max(303, min(containerWidth, 40vh)) —
           legacy parity. Capping at the container's own width
           keeps portrait media from over-extending vertically;
           the 40vh ceiling limits very tall viewports. Every
           slide fits inside via `object-contain`, blurred
           backdrop fills any gutter. */
        className="relative w-full shrink-0 overflow-hidden bg-zinc-100"
        style={{
          // Fall back to the 40vh / 303 floor on the first paint
          // before `heroHeight` resolves; client-only.
          height: heroHeight != null ? `${heroHeight}px` : 'min(40vh, 100vw)',
          minHeight: 303,
        }}
      >
        {current ? (
          <>
            {/* Heavy-blurred backdrop of the active image, fills the
                letterbox area. aria-hidden so AT skips it. */}
            <div
              aria-hidden
              className="absolute inset-0 scale-110 overflow-hidden blur-3xl"
            >
              <PopImage
                src={current}
                alt=""
                fill
                resizing={{ width: 192, height: 192, quality: 70 }}
                resizeMode="cover"
              />
            </div>

            {/* Scroll-snap track — every slide mounted side-by-side. Native
                drag / wheel / touch scroll changes the image; no swap-flash. */}
            <div
              ref={trackRef}
              onScroll={onScroll}
              className="absolute inset-0 flex snap-x snap-mandatory overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {slides.map((slide, i) => {
                const key = `${slide.id ?? ''}::${slide.url}::${i}`;
                const trigger = (
                  <button
                    type="button"
                    aria-label={
                      slide.type === 'video'
                        ? `Video ${i + 1}`
                        : `Zoom image ${i + 1}`
                    }
                    className={cn(
                      'relative h-full w-full shrink-0 snap-center',
                      slide.type === 'video'
                        ? 'cursor-default'
                        : 'cursor-zoom-in',
                    )}
                  >
                    {slide.type === 'video' ? (
                      /* Auto-playing muted preview — no native
                         browser controls (legacy parity: the
                         carousel video loops silently as a
                         thumbnail-style media; full playback +
                         sound lives in the AI Creator Studio). */
                      <video
                        src={slide.url}
                        className="absolute inset-0 h-full w-full object-contain"
                        muted
                        loop
                        autoPlay
                        playsInline
                      />
                    ) : i === 0 ? (
                      <PopImage
                        src={slide.url}
                        alt={name}
                        fill
                        resizeMode="contain"
                        resizing={SLIDE_RESIZE}
                        priority
                      />
                    ) : (
                      /* Non-LCP slides: `SLIDE_RESIZE` (`fit: 'inside'`) keeps the
                         source's full aspect ratio. A square `fit: cover` crop
                         would shear portrait media's top + bottom, making every
                         slide after index 0 look cropped. */
                      <PopImage
                        src={slide.url}
                        alt={name}
                        fill
                        resizeMode="contain"
                        resizing={SLIDE_RESIZE}
                      />
                    )}
                  </button>
                );

                // Videos don't open the zoom viewer (RN parity — the lightbox
                // gallery is image-only); they stay inline. Image slides become
                // `PhotoView` triggers of the shared `PhotoProvider` gallery.
                return slide.type === 'video' ? (
                  <Fragment key={key}>{trigger}</Fragment>
                ) : (
                  <PhotoView
                    key={key}
                    src={buildResizeUrl(slide.url, VIEWER_RESIZE)}
                  >
                    {trigger}
                  </PhotoView>
                );
              })}
            </div>
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-zinc-400">
            <ImageOff className="size-12" />
          </div>
        )}

        {floatingActions != null ? (
          <div className="absolute right-4 top-4 z-10 flex items-center">
            {floatingActions}
          </div>
        ) : null}

        {/* Active-slide overlay — the PDP Wear-With-AI integration
            uses this to render favorite + visibility chips on the
            current slide when it's tagged `avatar-me`. */}
        {slideOverlay && activeSlide ? (
          <div className="pointer-events-none absolute inset-x-3 top-3 z-10 flex justify-start [&>*]:pointer-events-auto">
            {slideOverlay(activeSlide, safeIndex)}
          </div>
        ) : null}

        {count > 1 ? (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Previous image"
              className="bg-sink/30 text-content-1 hover:bg-sink/50 absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full p-2 backdrop-blur-sm transition"
            >
              <ChevronLeft className="size-5" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next image"
              className="bg-sink/30 text-content-1 hover:bg-sink/50 absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full p-2 backdrop-blur-sm transition"
            >
              <ChevronRight className="size-5" />
            </button>

            {/* Instagram / TikTok / X pagination pattern:
                fixed-width pill of up to 7 dots, active dot
                pinned near the center, edge dots taper smaller
                to indicate "more to scroll." The window only
                shifts when the active index moves into the last
                two positions of the visible range — so once
                you're past the first few slides the active dot
                stays at position 3-of-5 (or 4-of-7), giving a
                fixed-anchor feel instead of the active dot
                jumping every swipe. */}
            {(() => {
              const windowSize = Math.min(7, count);
              // Distance from each edge that triggers a window shift.
              // For windowSize=7 → edgeBuffer=2 means active stays in
              // the middle 3 slots (indices 2..4) before shifting.
              const edgeBuffer = Math.max(1, Math.floor(windowSize / 3));

              // Compute window start so the active index sits at
              // `edgeBuffer` from the leading edge once we've moved
              // past the first `edgeBuffer` slides.
              let start: number;
              if (safeIndex < edgeBuffer) {
                start = 0;
              } else if (safeIndex > count - 1 - edgeBuffer) {
                start = count - windowSize;
              } else {
                start = safeIndex - edgeBuffer;
              }
              start = Math.max(0, Math.min(start, count - windowSize));
              const end = start + windowSize;

              return (
                <div className="bg-sink/40 absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-full px-1.5 py-1 backdrop-blur-sm">
                  {slides.slice(start, end).map((slide, j) => {
                    const i = start + j;
                    const active = i === safeIndex;
                    // Edge-taper: smallest at the very edge,
                    // medium one step in, full size in the
                    // middle. Only applied when there are more
                    // slides beyond the window in that
                    // direction (so the leftmost dot only
                    // tapers if there are unseen slides to its
                    // left).
                    const distFromLeft = j;
                    const distFromRight = windowSize - 1 - j;
                    const tapersLeft = start > 0;
                    const tapersRight = end < count;
                    const isEdge =
                      (tapersLeft && distFromLeft === 0) ||
                      (tapersRight && distFromRight === 0);
                    const isNearEdge =
                      (tapersLeft && distFromLeft === 1) ||
                      (tapersRight && distFromRight === 1);

                    return (
                      <button
                        key={`dot::${slide.id ?? ''}::${slide.url}::${i}`}
                        type="button"
                        onClick={() => scrollTo(i)}
                        aria-label={`Image ${i + 1}`}
                        className="flex size-3 items-center justify-center"
                      >
                        <span
                          aria-hidden
                          className={cn(
                            'rounded-full transition-all',
                            active
                              ? 'size-1.5 bg-white'
                              : isEdge
                                ? 'bg-raise/40 size-[3px]'
                                : isNearEdge
                                  ? 'bg-raise/50 size-[5px]'
                                  : 'bg-raise/50 size-1',
                          )}
                        />
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </>
        ) : null}
      </div>
    </PhotoProvider>
  );
}
